// @flow

import type {
  Duration,
  Accessory,
  ExtraContract,
  Claimfree,
  Mileage,
} from '../../../types/pdb';
import type { State } from '../types';
import { getActivePackage } from '../../configuration/actives/selectors/package';

import {
  getPdbItem,
} from './main';

type GlobalState = {
  pdb: State,
};

export const getActiveExtras = (state: GlobalState): ?any[] => {
  let activePackage;
  try {
    // $FlowFixMe
    activePackage = getActivePackage(state);
  } catch (error) {
    return null;
  }
  if (!activePackage || !activePackage.extras || !activePackage.extras.length) {
    return null;
  }
  return activePackage.extras;
};

const filterExtras = (state: GlobalState, allExtras: any[]): any[] => {
  const activeExtras = getActiveExtras(state);
  if (!activeExtras) {
    return allExtras;
  }
  return allExtras.reduce((acc, extra) => {
    if (activeExtras && activeExtras.find(activeExtra => activeExtra === extra.id)) {
      acc.push(extra);
    }
    return acc;
  }, []);
};

export const getAccessories = (state: GlobalState, pdbId: ?string = null): Accessory[] => {
  const pdbItem = getPdbItem(state, pdbId);
  return filterExtras(state, (pdbItem.extras && pdbItem.extras.extra) || []);
};

export const getExtraContracts = (state: GlobalState, pdbId: ?string = null): ExtraContract[] => {
  const pdbItem = getPdbItem(state, pdbId);
  return filterExtras(state, (pdbItem.extras && pdbItem.extras.contract) || []);
};

export const getDurations = (state: GlobalState, pdbId: ?string = null): Duration[] => {
  const pdbItem = getPdbItem(state, pdbId);
  return (pdbItem.extras && pdbItem.extras.duration) || [];
};

export const getClaimFrees = (state: GlobalState, pdbId: ?string = null): Claimfree[] => {
  const pdbItem = getPdbItem(state, pdbId);
  return (pdbItem.extras && pdbItem.extras.claimfree) || [];
};

export const getMileages = (state: GlobalState, pdbId: ?string = null): Mileage[] => {
  const pdbItem = getPdbItem(state, pdbId);
  // $FlowFixMe
  return (pdbItem.extras && pdbItem.extras.mileage)
    || (pdbItem.extras && pdbItem.extras.milage)
    || [];
};

export const getAccessoryById = (
  state: GlobalState,
  pdbId: ?string = null,
  accessoryId: number,
): ?Accessory => (getAccessories(state, pdbId) || []).find(acc => acc.id === accessoryId);

export const getExtraContractById = (
  state: GlobalState,
  pdbId: ?string = null,
  extraContractId: number,
): ?ExtraContract => (
  getExtraContracts(state, pdbId) || []).find(acc => acc.id === extraContractId);


export const getDurationName = (
  state: GlobalState,
  pdbId: ?string = null,
  index?: number = 0,
): string => {
  const durations = getDurations(state, pdbId);
  if (!durations || !durations[index]) {
    throw Error('Couldn\'t find duration');
  }

  return durations[index].name;
};

export const getDuration = (
  state: GlobalState,
  pdbId: ?string = null,
  index?: number = 0,
): ?string => {
  const durations = getDurations(state, pdbId);
  if (!durations) {
    return null;
  }

  if (!durations[index]) {
    throw Error('Couldn\'t find duration');
  }

  return durations[index].duration;
};

export const getDurationById = (
  state: GlobalState,
  pdbId: ?string = null,
  durartionId?: number = 0,
): ?Duration => {
  const durations = getDurations(state, pdbId);
  if (!durations) {
    return null;
  }

  const duration = durations.find(dur => dur.id === durartionId);
  if (!duration) {
    throw Error('Couldn\'t find duration by id');
  }

  return duration;
};

export const getDefaultDuration = (
  state: GlobalState,
  pdbId: ?string = null,
): ?Duration => {
  const goal = 0;
  const durations = getDurations(state, pdbId);

  if (!durations && durations.length === 0) {
    return null;
  }

  if (durations.length === 1) {
    return durations[0];
  }

  const closest = durations.reduce((prev, curr) => {
    const currPrice = Math.abs(parseInt(curr.price, 10) - goal);
    const prevPrice = Math.abs(parseInt(prev.price, 10) - goal);
    return (currPrice < prevPrice ? curr : prev);
  });
  return closest;
};

export const getDefaultDurationValue = (
  state: GlobalState,
  pdbId: ?string = null,
): ?string => {
  const duration = getDefaultDuration(state, pdbId);

  if (!duration) {
    return null;
  }

  return duration.duration;
};

export const getDurationDescription = (
  state: GlobalState,
  pdbId: ?string = null,
  index?: number = 0,
): string => {
  const durations = getDurations(state, pdbId);
  if (!durations[index]) {
    throw Error('Couldn\'t find duration');
  }

  return durations[index].description;
};

export const getMileageDescription = (
  state: GlobalState,
  pdbId: ?string = null,
  index?: number = 0,
): ?string => {
  const mileages = getMileages(state, pdbId);

  if (!mileages) {
    return null;
  }

  if (!mileages[index]) {
    throw Error('Couldn\'t find mileageDescription');
  }

  return mileages[index].description;
};

export const getMileage = (
  state: GlobalState,
  pdbId: ?string = null,
  index?: number = 0,
) => {
  const mileages = getMileages(state, pdbId);

  if (!mileages) {
    return null;
  }

  if (!mileages[index]) {
    throw Error('Couldn\'t find mileage');
  }

  return mileages[index].mileage;
};

export const getMileageById = (
  state: GlobalState,
  pdbId: ?string = null,
  mileageId?: number = 0,
): ?Mileage => {
  const mileages = getMileages(state, pdbId);
  if (!mileages || mileages.length === 0) {
    return null;
  }

  const mileage = mileages.find(m => m.id === mileageId);
  if (!mileage) {
    throw Error('Couldn\'t find mileage by id');
  }

  return mileage;
};

export const getMileageAsNumber = (
  state: GlobalState,
  pdbId: ?string = null,
) => {
  const mileages = getMileages(state, pdbId);

  if (!mileages || mileages.length === 0) {
    return null;
  }

  const mileage = mileages.find(mileageData => mileageData.price === '0');

  if (!mileage) {
    throw Error('Couldn\'t find mileageAsNumber');
  }

  return mileage.mileageAsNumber;
};

export const getDefaultMileage = (
  state: GlobalState,
  pdbId: ?string = null,
): ?Mileage => {
  const goal = 0;
  const mileages = getMileages(state, pdbId);

  if (!mileages || mileages.length === 0) {
    return null;
  }

  if (mileages.length === 1) {
    return mileages[0];
  }

  const closest = mileages.reduce((prev, curr) => {
    const currPrice = Math.abs(parseInt(curr.price, 10) - goal);
    const prevPrice = Math.abs(parseInt(prev.price, 10) - goal);
    return (currPrice < prevPrice ? curr : prev);
  });
  return closest;
};

export const getDefaultMileageValue = (
  state: GlobalState,
  pdbId: ?string = null,
): ?string => {
  const mileage = getDefaultMileage(state, pdbId);

  if (!mileage) {
    return null;
  }

  return mileage.mileage;
};

export const getDefaultMileageName = (
  state: GlobalState,
  pdbId: ?string = null,
): ?string => {
  const mileage = getDefaultMileage(state, pdbId);

  if (!mileage) {
    return null;
  }

  return mileage.name;
};

export const getMileageName = (
  state: GlobalState,
  pdbId: ?string = null,
  index?: number = 0,

): string => {
  const mileages = getMileages(state, pdbId);
  if (!mileages || !mileages[index]) {
    throw Error('Couldn\'t find mileageName');
  }

  return mileages[index].name;
};

export const getMileageByKilometers = (
  state: GlobalState,
  pdbId: ?string = null,
  kilometers: number,
) => {
  const mileages = [...getMileages(state, pdbId)];
  // sort all milages from high to low. Now keep matching the kilometers given
  // to the lowering mileages. Return the new one if it's smaller than the request
  // we will end up with the mileage that contains enough kilometers for our request.
  // we always start with the highest to allow a gazillion kilometers
  return mileages.sort((a, b) => (a.mileageAsNumber > b.mileageAsNumber ? -1 : 1))
    .reduce((prev, next) => {
      if (kilometers <= next.mileageAsNumber) {
        return next;
      }
      return prev;
    }, mileages[0]);
};

export const getClaimFreeById = (
  state: GlobalState,
  pdbId: ?string = null,
  claimFreeId?: number = 0,
): ?Claimfree => {
  const claimfrees = getClaimFrees(state, pdbId);
  if (!claimfrees) {
    return null;
  }

  const claimfree = claimfrees.find(c => c.id === claimFreeId);
  if (!claimfree) {
    throw Error('Couldn\'t find claimfree by id');
  }

  return claimfree;
};
